import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { fieldReferenceOptions, fieldReviewReference } from '../field-reference-options';

import type { FieldDestination } from '../field-overview';
import type { FieldSnapshot } from '@mio/shared';
import type { ReactNode } from 'react';

interface CitationNode {
  type: string;
  value?: string;
  url?: string;
  children?: CitationNode[];
  data?: { hProperties: { 'data-field-citation': string } };
}

interface CitationReference {
  label: string;
  destination?: FieldDestination | undefined;
}

const citationPrefix = '#field-citation-';

/** Resolve citations in prose, never code, authored links or unknown references. */
function citationPlugin(resolve: (id: string) => CitationReference | undefined) {
  function citation(id: string, reference: CitationReference): CitationNode {
    return {
      type: 'link',
      url: `${citationPrefix}${encodeURIComponent(id)}`,
      data: { hProperties: { 'data-field-citation': id } },
      children: [{ type: 'text', value: reference.label }],
    };
  }
  function prose(value: string, bracketBefore = false, bracketAfter = false): CitationNode[] {
    const result: CitationNode[] = [];
    let end = 0;
    // Keep internal URI/version punctuation intact so unknown extended IDs cannot
    // become actionable partial references. Strip only surrounding prose punctuation.
    for (const token of value.matchAll(/\S+/g)) {
      // A bracket group can split an unknown compound token; it does not establish
      // a whitespace boundary for the standalone reference on either side.
      if (
        (bracketBefore && token.index === 0) ||
        (bracketAfter && token.index + token[0].length === value.length)
      )
        continue;
      const leading = /^[([{"'“‘]*/.exec(token[0])?.[0].length ?? 0;
      const trailing = /[)\]}"'”’,;!?]*\.?[)\]}"'”’,;!?]*$/.exec(token[0])?.[0].length ?? 0;
      const id = token[0].slice(leading, token[0].length - trailing);
      const reference = id.startsWith('review-') ? resolve(id) : undefined;
      if (!reference?.destination) continue;
      const start = token.index + leading;
      result.push({ type: 'text', value: value.slice(end, start) });
      result.push(citation(id, reference));
      end = start + id.length;
    }
    result.push({ type: 'text', value: value.slice(end) });
    return result;
  }
  return () => (tree: CitationNode) => {
    function visit(node: CitationNode) {
      if (
        !node.children ||
        ['link', 'linkReference', 'image', 'imageReference'].includes(node.type)
      )
        return;
      node.children = node.children.flatMap((child) => {
        if (child.type !== 'text' || !child.value) {
          visit(child);
          return [child];
        }
        const result: CitationNode[] = [];
        let end = 0;
        for (const group of child.value.matchAll(/\[[^\]\n]+\]/g)) {
          const start = group.index;
          result.push(...prose(child.value.slice(end, start), end > 0, true));
          let tokenEnd = 0;
          for (const token of group[0].matchAll(/[^\][,;\s]+/g)) {
            const reference = resolve(token[0]);
            if (!reference) continue;
            result.push({ type: 'text', value: group[0].slice(tokenEnd, token.index) });
            result.push(citation(token[0], reference));
            tokenEnd = token.index + token[0].length;
          }
          result.push({ type: 'text', value: group[0].slice(tokenEnd) });
          end = start + group[0].length;
        }
        result.push(...prose(child.value.slice(end), end > 0));
        return result;
      });
    }
    visit(tree);
  };
}

function MessageLink({ children, href }: { children?: ReactNode; href?: string | undefined }) {
  return href && /^https?:\/\//i.test(href) ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-4"
    >
      {children}
    </a>
  ) : (
    <span>{children}</span>
  );
}

const components: Components = {
  a: MessageLink,
  // Colleague messages cannot load remote tracking images into the conversation.
  img: ({ alt }) => <span>{alt}</span>,
  table: ({ children }) => (
    <div
      className="max-w-full overflow-x-auto"
      tabIndex={0}
      role="region"
      aria-label="Message table"
    >
      <table className="w-full border-collapse text-left">{children}</table>
    </div>
  ),
};

export function FieldChatMarkdown({
  source,
  snapshot,
  onNavigate,
}: {
  source: string;
  snapshot?: FieldSnapshot | undefined;
  onNavigate?: ((destination: FieldDestination) => void) | undefined;
}) {
  const known = new Map(
    (snapshot ? fieldReferenceOptions(snapshot) : []).map((item) => [item.id, item.label]),
  );
  const resolve = (id: string): CitationReference | undefined => {
    if (!snapshot) return undefined;
    const review = fieldReviewReference(snapshot, id);
    if (review) return review;
    const label = known.get(id);
    if (!label) return undefined;
    return {
      label,
      destination: snapshot.discovery?.findings.some((finding) => finding.id === id)
        ? { section: 'engagement', focusId: id }
        : undefined,
    };
  };
  const citationComponents: Components = {
    ...components,
    a: (props) => {
      const id = props.node?.properties['data-field-citation'];
      const reference = typeof id === 'string' ? resolve(id) : undefined;
      if (!reference) return <MessageLink {...props} />;
      const destination = reference.destination;
      return destination && onNavigate ? (
        <button
          type="button"
          className="text-left underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2"
          onClick={() => onNavigate(destination)}
        >
          {reference.label}
        </button>
      ) : (
        <span>{reference.label}</span>
      );
    },
  };
  return (
    <div className="field-chat-markdown">
      <ReactMarkdown
        remarkPlugins={snapshot ? [remarkGfm, citationPlugin(resolve)] : [remarkGfm]}
        components={snapshot ? citationComponents : components}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}

/** Calendar dates come from the transcript; they are not simulated engagement days. */
function conversationDateKey(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

export function FieldConversationDate({
  value,
  previous,
}: {
  value: string;
  previous?: string | undefined;
}) {
  if (previous && conversationDateKey(value) === conversationDateKey(previous)) return null;
  const date = new Date(value);
  const label = Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });
  return (
    <div className="field-message-day">
      <time dateTime={value}>{label}</time>
    </div>
  );
}

export function FieldConversationTime({ value }: { value: string }) {
  const date = new Date(value);
  const valid = !Number.isNaN(date.getTime());
  return (
    <time dateTime={value} title={valid ? date.toLocaleString() : value}>
      {valid ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : value}
    </time>
  );
}
